import axios from 'axios';

// Use relative URL in production (when served via Nginx), or explicit URL in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const fetchSites = async (siteIds = null) => {
  let url = '/sites';
  if (siteIds && Array.isArray(siteIds) && siteIds.length > 0) {
    const siteIdsParam = siteIds.join(',');
    url += `?siteIds=${siteIdsParam}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const fetchMetrics = async () => {
  const response = await api.get('/metrics');
  return response.data;
};

export const fetchMetricsBySite = async (siteId) => {
  const response = await api.get(`/metrics/${siteId}`);
  return response.data;
};

export const fetchTrends = async (siteId, metricType = 'load_average', date = null) => {
  let url = `/trends/${siteId}/${metricType}`;
  if (date) {
    url += `?date=${date}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const fetchAlerts = async () => {
  const response = await api.get('/alerts');
  return response.data;
};

export const fetchSummary = async () => {
  const response = await api.get('/summary');
  return response.data;
};

export const dismissAlert = async (alertId) => {
  const response = await api.post(`/alerts/${alertId}/dismiss`, { permanent: true });
  return response.data;
};

export const dismissAlertTemporarily = async (alertId, hours) => {
  const response = await api.post(`/alerts/${alertId}/dismiss`, { 
    permanent: false, 
    hours: hours 
  });
  return response.data;
};

export const fetchThresholds = async (siteId) => {
  const response = await api.get(`/thresholds/${siteId}`);
  return response.data;
};

export const updateThresholds = async (siteId, thresholds) => {
  const response = await api.put(`/thresholds/${siteId}`, thresholds);
  return response.data;
};

export const fetchAllThresholds = async () => {
  const response = await api.get('/thresholds');
  return response.data;
};

export const fetchAlertSettings = async (siteId) => {
  const response = await api.get(`/alert-settings/${siteId}`);
  return response.data;
};

export const fetchAllAlertSettings = async () => {
  const response = await api.get('/alert-settings');
  return response.data;
};

export const updateAlertSettings = async (siteId, settings, userName = null) => {
  // Resolve actor name from argument or stored auth session
  let resolvedUser = userName;
  if (!resolvedUser) {
    try {
      const stored = localStorage.getItem('authData');
      if (stored) {
        const auth = JSON.parse(stored);
        resolvedUser = auth.fullName || auth.userName || auth.username || auth.name || null;
      }
    } catch (_) {
      // ignore
    }
  }

  const payload = { ...settings };
  // Use dedicated body field (headers cannot carry non-Latin names like Hebrew)
  delete payload.userName;
  if (resolvedUser) {
    payload.changedBy = resolvedUser;
  } else {
    delete payload.changedBy;
  }

  const response = await api.put(`/alert-settings/${siteId}`, payload);
  return response.data;
};

export const fetchAlertsHistory = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.siteId) params.append('siteId', filters.siteId);
  if (filters.type) params.append('type', filters.type);
  if (filters.severity) params.append('severity', filters.severity);
  if (filters.resolved !== undefined) params.append('resolved', filters.resolved);
  if (filters.search) params.append('search', filters.search);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.sortField) params.append('sortField', filters.sortField);
  if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  
  const response = await api.get(`/alerts-history?${params.toString()}`);
  return response.data;
};

export const fetchAlertStatistics = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.siteId) params.append('siteId', filters.siteId);
  if (filters.type) params.append('type', filters.type);
  if (filters.severity) params.append('severity', filters.severity);
  if (filters.resolved !== undefined) params.append('resolved', filters.resolved);
  if (filters.search) params.append('search', filters.search);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  
  const response = await api.get(`/alerts-history/stats?${params.toString()}`);
  return response.data;
};

export const fetchAllAlertTypes = async () => {
  const response = await api.get('/alerts-history/types');
  return response.data;
};

// Resolve alert by history ID (for alerts history page)
export const resolveAlertByHistoryId = async (historyId, userName = null) => {
  const payload = { userName: userName || null };
  const response = await api.post(`/alerts-history/${historyId}/resolve`, payload);
  return response.data;
};

export const resolveAlert = async (alertId, userName = null) => {
  // Always send userName in payload, even if null (server will default to 'System')
  const payload = { userName: userName || null };
  
  // Try resolving by alertId first (for active alerts from /api/alerts endpoint)
  // alertId from active alerts is a hash (large number like 1325968860)
  // alertId from history is a database ID (smaller number like 344)
  // We'll try alertId endpoint first, and if it fails, try history endpoint
  try {
    const response = await api.post(`/alerts/${alertId}/resolve`, payload);
    return response.data;
  } catch (error) {
    // If that fails, try the history ID endpoint (for alerts history page)
    // This handles cases where alertId is actually a history ID
    // Always try history endpoint if alertId endpoint fails (regardless of error code)
    try {
      const response = await api.post(`/alerts-history/${alertId}/resolve`, payload);
      return response.data;
    } catch (historyError) {
      // If history endpoint also fails, throw the original error
      throw error;
    }
  }
};

export const resolveAlerts = async (alertIds, userName = null) => {
  const payload = { alertIds };
  if (userName) {
    payload.userName = userName;
  }
  const response = await api.post('/alerts-history/resolve-multiple', payload);
  return response.data;
};

/**
 * Login function - sends request to server which proxies to AWS API
 * Password hashing is done on the server to avoid CORS issues
 */
export const login = async (username, password) => {
  try {
    console.log('Sending login request to server...');
    
    // Send login request to local server (which will proxy to AWS API)
    const response = await api.post('/login', {
      userName: username,
      password: password // Server will hash this
    });
    
    console.log('Login API response status:', response.status);
    return response.data;
  } catch (error) {
    console.error('Login API error:', error);
    if (error.response) {
      // API returned an error response
      console.error('API error response:', error.response.status, error.response.data);
      const errorMessage = error.response.data?.error || error.response.data?.message || `Login failed (${error.response.status})`;
      throw new Error(errorMessage);
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received:', error.request);
      throw new Error('Network error - please check your connection');
    } else {
      // Something else happened
      console.error('Unexpected error:', error.message);
      throw error;
    }
  }
};

export default api;

